// frontend/admin-portal/src/components/orders/CustomerModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { customerService } from "../../services/customerService";
import { useSettings } from "../../contexts/SettingsContext";
import { orderService } from "../../services/orderService";

const EMPTY = {
  type: "individual",
  name: "",
  contactName: "",
  companyName: "",
  phone: "",
  altPhone: "",
  email: "",
  website: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  region: "",
  country: "",
  postalCode: "",
  taxId: "",
  registrationNo: "",
  paymentTermsDays: 0,
  creditLimit: 0,
  currency: "USD",
  notes: "",
  isActive: true,
};

export default function CustomerModal({ open, customer, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { get } = useSettings();

  useEffect(() => {
    if (!open) return;
    setError("");
    if (customer) {
      setForm({ ...EMPTY, ...customer });
    } else {
      setForm({
        ...EMPTY,
        currency: get("finance.baseCurrency", "USD"),
        paymentTermsDays: Number(get("invoices.dueDays", 30)),
      });
    }
  }, [open, customer, get]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      let result;
      if (customer?.id)
        result = await customerService.update(customer.id, form);
      else result = await customerService.create(form);
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={customer?.id ? `Edit: ${customer.name}` : "New Customer"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            {customer?.id ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="row g-3">
        <div className="col-md-4">
          <label className="fh-input-label">Type</label>
          <select
            className="fh-input"
            value={form.type}
            onChange={(e) => set("type", e.target.value)}
          >
            <option value="individual">Individual</option>
            <option value="business">Business</option>
            <option value="government">Government</option>
          </select>
        </div>
        <div className="col-md-8">
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        {form.type === "business" && (
          <div className="col-md-6">
            <Input
              label="Company Name"
              value={form.companyName}
              onChange={(e) => set("companyName", e.target.value)}
            />
          </div>
        )}
        <div className="col-md-6">
          <Input
            label="Contact Person"
            value={form.contactName}
            onChange={(e) => set("contactName", e.target.value)}
          />
        </div>

        <div className="col-md-4">
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="Alt Phone"
            value={form.altPhone}
            onChange={(e) => set("altPhone", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </div>

        <div className="col-12">
          <Input
            label="Address Line 1"
            value={form.addressLine1}
            onChange={(e) => set("addressLine1", e.target.value)}
          />
        </div>
        <div className="col-12">
          <Input
            label="Address Line 2"
            value={form.addressLine2}
            onChange={(e) => set("addressLine2", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="City"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="Region"
            value={form.region}
            onChange={(e) => set("region", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="Country"
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
          />
        </div>

        <div className="col-md-6">
          <Input
            label="Tax ID"
            value={form.taxId}
            onChange={(e) => set("taxId", e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <Input
            label="Registration No."
            value={form.registrationNo}
            onChange={(e) => set("registrationNo", e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
